import {
  useCallback,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import StandardAttribute from "../../../components/Attributes/StandardAttribute";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { AppContext } from "../../../Contexts/Contexts";
import DomUtils from "../../../utils/dom.utils";
import IdUtils from "../../../utils/id.utils";
import RowNode from "../../../components/Dnd/RowNode";
import BrowseConstants from "../../../store/browse.constants";

const ClassificationPanel = ({
  documents,
  setDocuments,
  controlledDocumentIndex,
}) => {
  // console.log("Classification Panel");
  const { alert } = useContext(AppContext);
  const [rows, setRows] = useState([]);
  const [listWidth, setListWidth] = useState(0);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(-1);
  const [selectedPageContexts, setSelectedPageContexts] = useState(new Set());
  const documentIndexToRowIndex = useRef({});
  const listRef = useRef(null);
  const resizeTimeout = useRef(null);

  const lowConfDocIndexes = useMemo(() => {
    return documents
      .map((doc, index) => ({ index, doc }))
      .filter(({ doc }) => doc.confidence < doc.threshold)
      .map(({ index }) => index);
  }, [documents]);

  const rerenderList = useCallback(() => {
    listRef.current?.resetAfterIndex(0);
  }, []);

  const onResize = useCallback((width) => {
    if (resizeTimeout.current) {
      clearTimeout(resizeTimeout.current);
    }
    resizeTimeout.current = setTimeout(() => {
      const scrollbarWidth = 12;
      const px = 20;
      const listWidth = width - scrollbarWidth - px;
      setListWidth(listWidth);
    }, 500);
  }, []);

  const toggleSelectedPageContexts = useCallback((pageContext) => {
    setSelectedPageContexts((selectedPageContexts) => {
      const newSelectedPageContexts = new Set(selectedPageContexts);
      if (newSelectedPageContexts.has(JSON.stringify(pageContext))) {
        newSelectedPageContexts.delete(JSON.stringify(pageContext));
      } else {
        newSelectedPageContexts.add(JSON.stringify(pageContext));
      }
      return newSelectedPageContexts;
    });
  }, []);

  const getDocumentDimensions = useCallback((document, listWidth) => {
    const p = 8;
    const rowGap = 0;
    const pyPage = 16;
    const headerHeight = 28;
    const autocompleteHeight = 45.13;
    const autocompleteWidth = 200;
    const minDocWidth = p + autocompleteWidth;
    const baseWidth = p + BrowseConstants.DROPZONE_WIDTH;
    const baseHeight = rowGap + p + headerHeight + p + autocompleteHeight;
    const pageWidth =
      BrowseConstants.THUMBNAIL_WIDTH + BrowseConstants.DROPZONE_WIDTH;
    const pageHeight = pyPage + BrowseConstants.THUMBNAIL_HEIGHT;
    const maxPagesPerLine =
      Math.floor((listWidth - baseWidth) / pageWidth) || 1;
    const width = Math.max(
      (Math.min(document.pages.length, maxPagesPerLine) || 1) * pageWidth +
        baseWidth,
      minDocWidth
    );
    const numLines = Math.ceil(document.pages.length / maxPagesPerLine) || 1;
    const height = numLines * pageHeight + baseHeight;
    return { width, height };
  }, []);

  const updateRows = useCallback(() => {
    if (listWidth < 300) {
      documentIndexToRowIndex.current = {};
      setRows([]);
      return;
    }
    const gap = 4;
    const rows = [];
    let i = 0;
    let rowIndex = 0;
    const indexMap = {};
    while (i < documents.length) {
      const row = {
        documentContexts: [],
        width: 0,
        height: 0,
      };
      while (i < documents.length) {
        const { width, height } = getDocumentDimensions(
          documents[i],
          listWidth
        );
        if (row.width + width + gap > listWidth) break;
        row.documentContexts.push({
          documentIndex: i,
          document: documents[i],
        });
        indexMap[i] = rowIndex;
        row.width += width + gap;
        if (height > row.height) row.height = height;
        i++;
      }
      rows.push(row);
      rowIndex++;
    }
    documentIndexToRowIndex.current = indexMap;
    setRows(rows);
  }, [getDocumentDimensions, documents, listWidth]);

  const onClassify = useCallback(
    (document, type) => {
      if (!type) return;
      setDocuments((documents) =>
        documents.map((prev, i) => {
          if (prev._id === document._id) {
            const doc = {
              ...prev,
              type,
              confidence: 101,
              explorer: {
                ...prev.explorer,
                index: i,
                name: `(${i + 1}) ${type}`,
              },
            };
            return doc;
          }
          return prev;
        })
      );
    },
    [setDocuments]
  );

  const onDelete = useCallback(
    (documentIndex) => {
      setDocuments((documents) => {
        const newDocuments = documents.filter((_, i) => i !== documentIndex);
        return newDocuments.map((doc, index) => {
          const warning = doc.confidence < doc.threshold ? "! " : "";
          doc.explorer = {
            ...doc.explorer,
            index,
            name: `(${index + 1}) ${warning} ${doc.type}`,
          };
          return doc;
        });
      });
    },
    [setDocuments]
  );

  const onSplit = useCallback(
    ({ documentIndex, pageIndex, position }) => {
      const document = documents[documentIndex];
      const pages = document.pages;
      const splitIndex = position === "left" ? pageIndex - 1 : pageIndex;
      if (splitIndex < 0 || splitIndex >= pages.length) {
        return;
      }
      const leftPages = pages.slice(0, splitIndex + 1);
      const rightPages = pages.slice(splitIndex + 1);

      const firstDocument = {
        ...document,
        pages: leftPages,
        _id: IdUtils.generateUuid(),
      };
      const secondDocument = {
        ...document,
        pages: rightPages,
        _id: IdUtils.generateUuid(),
      };

      setDocuments((documents) => {
        const updatedDocuments = [
          ...documents.slice(0, documentIndex),
          firstDocument,
          secondDocument,
          ...documents.slice(documentIndex + 1),
        ];
        updatedDocuments.forEach((document, index) => {
          document.index = index;
          const warning = document.confidence < document.threshold ? "! " : "";
          document.explorer = {
            ...document.explorer,
            index,
            name: `(${index + 1}) ${warning} ${document.type}`,
          };
        });
        return updatedDocuments;
      });
    },
    [documents, setDocuments]
  );

  const onPageDrop = useCallback(
    (item, dropZoneContext) => {
      const { type, draggedContext, selectedPageContexts, documentIndex } =
        item;
      const {
        documentIndex: targetDocumentIndex,
        pageIndex: targetPageIndex,
        position,
      } = dropZoneContext;
      let pageContexts;
      if (type === "page") {
        selectedPageContexts.add(JSON.stringify(draggedContext));
        pageContexts = Array.from(selectedPageContexts)
          .map((context) => JSON.parse(context))
          .sort((a, b) => {
            if (a.documentIndex === b.documentIndex) {
              return a.pageIndex - b.pageIndex;
            }
            return a.documentIndex - b.documentIndex;
          });
      }
      if (type === "document") {
        pageContexts = documents[documentIndex].pages.map((_, i) => ({
          documentIndex,
          pageIndex: i,
        }));
      }

      const pagesToMove = [];
      const newDocuments = [...documents];

      for (let i = pageContexts.length - 1; i >= 0; i--) {
        const { documentIndex, pageIndex } = pageContexts[i];
        const [removedPage] = newDocuments[documentIndex].pages.splice(
          pageIndex,
          1
        );
        pagesToMove.unshift(removedPage);
      }

      const insertIndex =
        position === "left" ? targetPageIndex : targetPageIndex + 1;
      newDocuments[targetDocumentIndex].pages.splice(
        insertIndex,
        0,
        ...pagesToMove
      );

      setDocuments(newDocuments);
    },
    [documents, setDocuments]
  );

  const hotkeys = useCallback(
    (e) => {
      if (DomUtils.hasPopup()) return;
      switch (e.key) {
        case "Enter":
        case "Tab":
          if (e.key === "Enter" && selectedDocumentIndex !== -1) {
            const doc = documents[selectedDocumentIndex];
            onClassify(doc, doc.type);
          }
          if (lowConfDocIndexes.length === 0) {
            alert.open(<div>No Documents to Review</div>);
            return;
          }
          const getDocIndex = (direction) => {
            if (direction === "next") {
              for (let index of lowConfDocIndexes) {
                if (index > selectedDocumentIndex) {
                  return index;
                }
              }
              return lowConfDocIndexes[0];
            } else {
              // prev
              for (let i = lowConfDocIndexes.length - 1; i >= 0; i--) {
                if (lowConfDocIndexes[i] < selectedDocumentIndex) {
                  return lowConfDocIndexes[i];
                }
              }
              return lowConfDocIndexes[lowConfDocIndexes.length - 1];
            }
          };
          const direction = e.shiftKey ? "prev" : "next";
          const nextDocIndex = getDocIndex(direction);
          setSelectedDocumentIndex(nextDocIndex);
          listRef.current.scrollToItem(
            documentIndexToRowIndex.current[nextDocIndex]
          );
          return;
        default:
          return;
      }
    },
    [selectedDocumentIndex, lowConfDocIndexes, alert, documents, onClassify]
  );

  useEffect(() => {
    if (controlledDocumentIndex >= 0) {
      setSelectedDocumentIndex(controlledDocumentIndex);
      const index = documentIndexToRowIndex.current[controlledDocumentIndex];
      if (index === undefined) return;
      listRef.current.scrollToItem(
        documentIndexToRowIndex.current[controlledDocumentIndex]
      );
    }
  }, [controlledDocumentIndex]);

  useEffect(() => updateRows(), [updateRows]);

  useEffect(() => {
    setSelectedPageContexts(new Set());
    rerenderList();
  }, [rows, rerenderList]);

  useEffect(() => {
    window.addEventListener("keydown", hotkeys);
    return () => {
      window.removeEventListener("keydown", hotkeys);
    };
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex-col gap-5 w-full h-full">
        <div
          className="flex gap-30 bg-lg pad-5-10 align-center"
          style={{ minHeight: "50px" }}
        >
          <StandardAttribute label="Total Documents" data={documents.length} />
          <StandardAttribute
            label="Total Pages"
            data={documents.reduce(
              (sum, document) => sum + document.pages.length,
              0
            )}
          />
          <StandardAttribute
            label="Documents to Review"
            data={lowConfDocIndexes.length}
          />
        </div>
        <div className="h-full bg-lg pt-1">
          <AutoSizer onResize={({ width }) => onResize(width)}>
            {({ width, height }) => {
              return (
                <List
                  ref={listRef}
                  className="scrollbar"
                  width={width}
                  height={height}
                  itemCount={rows.length}
                  itemSize={(i) => rows[i].height}
                  itemData={{
                    rows,
                    selectedPageContexts,
                    toggleSelectedPageContexts,
                    setSelectedPageContexts,
                    onClassify,
                    onPageDrop,
                    onSplit,
                    onDelete,
                    selectedDocumentIndex,
                    setSelectedDocumentIndex,
                  }}
                  children={RowNode}
                  estimatedItemSize={
                    rows.reduce((sum, row) => sum + row.height, 0) /
                    (rows.length || 1)
                  }
                />
              );
            }}
          </AutoSizer>
        </div>
      </div>
    </DndProvider>
  );
};

export default ClassificationPanel;
